import { Button, Grid } from "@mui/material";
import { EntityPanelActionHandler } from "types";
import { ReactNode, useEffect, useMemo, useContext } from "react";
import { isValidURL } from "utils";
import DownloadIcon from "@mui/icons-material/Download";
import { QRCodeContext, getActionRouteForDisplayMode } from "./QRCodesPanel";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useTranslation } from "react-i18next";
import { useOutletContext, useFetcher } from "react-router";
import { useBackButton } from "hooks/useBackButton";
import FormFrame from "components/layout/FormFrame";
import { AppContext } from "context/appContext";
import DetailsHeader from "components/layout/DetailsHeader";
import AuturaTextField from "components/inputs/AuturaTextField";

export const QRCodesForm = (): ReactNode => {
  const {
    qrCode,
    setQRCode,
    qrCodes,
    formMode,
    displayMode,
    onBack,
    handleDownloadQRCode,
    fetcherKey,
    entity,
  } = useOutletContext<QRCodeContext>();
  const { t } = useTranslation();
  const fetcher = useFetcher({ key: fetcherKey });
  const { state } = useContext(AppContext);

  // handle the browser back button
  useBackButton(onBack);

  useEffect(() => {
    setQRCode(qrCode);
    ValidatorForm.addValidationRule("isValidURL", (value) => {
      return isValidURL(value);
    });
  }, [qrCode]);

  const handleSubmit = () => {
    if (formMode === "create") {
      fetcher.submit(
        {
          body: JSON.stringify([
            ...qrCodes,
            {
              ...qrCode,
            },
          ]),
          handler:
            displayMode == "property"
              ? EntityPanelActionHandler.PROPERTY_CREATE
              : EntityPanelActionHandler.GROUP_CREATE,
        },
        {
          method: "POST",
          action: getActionRouteForDisplayMode({
            tenantID: state.tenant.id,
            entityID: entity.id,
            displayMode: displayMode,
          }),
          encType: "application/json",
        },
      );
    } else {
      fetcher.submit(
        {
          body: JSON.stringify({
            ...qrCode,
          }),
          handler: EntityPanelActionHandler.UPDATE,
        },
        {
          method: "POST",
          action: getActionRouteForDisplayMode({
            tenantID: state.tenant.id,
            entityID: entity.id,
            displayMode: displayMode,
          }),
          encType: "application/json",
        },
      );
    }
  };
  const submitButtonText = useMemo(() => {
    switch (formMode) {
      case "edit":
        return t("buttons.update", {
          name: t("qrCodes.label", { ns: "entities" }),
        });
      case "create":
        return t("buttons.create", {
          name: t("qrCodes.label", { ns: "entities" }),
        });
      default:
        return "";
    }
  }, [formMode]);

  return (
    <ValidatorForm onSubmit={handleSubmit}>
      <FormFrame loading={fetcher.state === "submitting"}>
        <DetailsHeader
          title={submitButtonText}
          onCancel={onBack}
          submitLabel={submitButtonText}
          readonly={!state.entityPermissions.property.update}
          disableSubmit={fetcher.state !== "idle"}
        />
        <Grid container width={"100%"} spacing={1.5} sx={{ mt: 3 }}>
          <Grid
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <AuturaTextField
              name="name"
              label={t("qrCodes.name", { ns: "entities" })}
              value={qrCode.data.title || ""}
              onChange={({ target: { value: v } }) =>
                setQRCode({
                  ...qrCode,
                  data: {
                    ...qrCode.data,
                    title: v,
                  },
                })
              }
              required
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <AuturaTextField
              name="description"
              label={t("qrCodes.description", { ns: "entities" })}
              value={qrCode.data.description || ""}
              onChange={({ target: { value: v } }) =>
                setQRCode({
                  ...qrCode,
                  data: {
                    ...qrCode.data,
                    description: v,
                  },
                })
              }
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              lg: 6,
            }}
          >
            <AuturaTextField
              name="content"
              label={t("qrCodes.data.content", { ns: "entities" })}
              validators={["isValidURL"]}
              value={qrCode.data.content || ""}
              errorMessages={[t("errors.invalidError", { name: "URL" })]}
              helperText={t("helpers.content", { ns: "qrCodes" })}
              onChange={({ target: { value: v } }) => {
                setQRCode({
                  ...qrCode,
                  data: {
                    ...qrCode.data,
                    content: v,
                  },
                });
              }}
              required
            />
          </Grid>
          {formMode === "edit" && (
            <Grid
              sx={{ mt: 2 }}
              size={{
                xs: 12,
                lg: 6,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => handleDownloadQRCode(qrCode)}
                startIcon={<DownloadIcon />}
              >
                {t("buttons.download", {
                  name: t("qrCodes.label", { ns: "entities" }),
                })}
              </Button>
            </Grid>
          )}
        </Grid>
      </FormFrame>
    </ValidatorForm>
  );
};
